import { useState, useRef, useEffect } from "react";
import VideoCrop from "./VideoCrop";
import VideoTimeline from "./VideoTimeline";

// import { useToast } from "@/hooks/use-toast";
import { trimVideoWithAudio } from "../../utils/ffmpegProcessor";

import { Button, Card, CardContent } from "@mui/material";
import {
  Rotate90DegreesCcw,
  Rotate90DegreesCw,
  Save,
} from "@mui/icons-material";
import styles from "./VideoTrimmer.module.css";
const VideoTrimmer = ({
  videoUrl,
  updatedVideoBlob,
  beforeUnload = () => {},
  onVideoDataLoaded = () => {},
  hasVideoPoster,
}) => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [cropData, setCropData] = useState(null);
  const [trimSettings, setTrimSettings] = useState({
    startTime: 0,
    endTime: 0,
    duration: 0,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [trimmedVideoUrl, setTrimmedVideoUrl] = useState(null);
  const videoRef = useRef(null);
  useEffect(() => {
    return () => {
      beforeUnload();
    };
  }, []);

  const handleVideoLoad = (duration) => {
    console.log("Video loaded with duration:", duration);

    // Handle problematic durations
    if (
      !isFinite(duration) ||
      isNaN(duration) ||
      duration <= 0 ||
      duration === Infinity
    ) {
      console.warn("Invalid video duration detected, attempting fallback");

      // Try to get duration from video element directly
      if (videoRef.current) {
        const video = videoRef.current;

        // Wait a bit and try again
        setTimeout(() => {
          const fallbackDuration = video.duration;
          console.log("Fallback duration attempt:", fallbackDuration);

          if (
            isFinite(fallbackDuration) &&
            fallbackDuration > 0 &&
            fallbackDuration !== Infinity
          ) {
            console.log("Using fallback duration:", fallbackDuration);
            setVideoDuration(fallbackDuration);
            setTrimSettings({
              startTime: 0,
              endTime: fallbackDuration,
              duration: fallbackDuration,
            });
          } else {
            // Last resort: estimate based on video properties or use default
            console.warn(
              "Could not determine video duration, using estimated duration"
            );
            const estimatedDuration = 60; // Default to 60 seconds for unknown duration
            setVideoDuration(estimatedDuration);
            setTrimSettings({
              startTime: 0,
              endTime: estimatedDuration,
              duration: estimatedDuration,
            });
            console.log(
              "Could not determine exact video duration. Using estimated duration for trimming."
            );
          }
        }, 1000);
      } else {
        console.error("The video duration could not be determined");
      }
      return;
    }

    setVideoDuration(duration);
    setTrimSettings({
      startTime: 0,
      endTime: duration,
      duration: duration,
    });
  };

  const handleTrimChange = (start, end) => {
    // Validate the inputs
    if (!isFinite(start) || !isFinite(end) || isNaN(start) || isNaN(end)) {
      console.error("Invalid trim values:", start, end);
      return;
    }

    console.log("Trim settings updated:", start, end);
    setTrimSettings({
      startTime: start,
      endTime: end,
      duration: end - start,
    });
  };

  const handleRotateClockwise = () => {
    setRotation((prev) => (prev + 90) % 360);
    setTrimmedVideoUrl(null); // Reset trimmed video when rotation changes
  };

  const handleRotateCounterClockwise = () => {
    setRotation((prev) => (prev - 90 + 360) % 360);
    setTrimmedVideoUrl(null); // Reset trimmed video when rotation changes
  };

  const handleExport = async () => {
    if (!videoUrl || trimSettings.duration <= 0) {
      console.log("Cannot export video: invalid URL or trim settings");

      return;
    }
    setProgress(0);
    setIsProcessing(true);

    try {
      // Use FFmpeg for processing with audio
      const blob = await trimVideoWithAudio(
        videoFile || videoUrl,
        trimSettings.startTime,
        trimSettings.endTime,
        rotation,
        cropData,
        (progress) => {
          setProgress(progress);
          // You can update a progress bar here if needed
        }
      );

      const url = URL.createObjectURL(blob);
      setTrimmedVideoUrl(url);
      const updatedFile = { blob, url };
      updatedVideoBlob(updatedFile);
    } catch (error) {
      console.error("Error processing video:", error);

      let errorMessage =
        "There was an error processing your video. Please try again.";

      if (error instanceof Error) {
        if (error.message.includes("timeout")) {
          errorMessage =
            "Processing timed out. Try with a shorter clip or smaller video file.";
        } else if (error.message.includes("Failed to initialize")) {
          errorMessage =
            "Failed to initialize video processor. Please refresh the page and try again.";
        } else {
          errorMessage = error.message;
        }
      }
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleReset = () => {
    setVideoFile(null);
    setVideoDuration(0);
    setRotation(0);
    setTrimSettings({ startTime: 0, endTime: 0, duration: 0 });
    setTrimmedVideoUrl(null);
    if (videoUrl && videoFile) {
      URL.revokeObjectURL(videoUrl);
    }
    if (trimmedVideoUrl) {
      URL.revokeObjectURL(trimmedVideoUrl);
    }
  };

  return (
    <div className={`${styles.video_trimmer_container}`}>
      {!videoUrl ? null : (
        <div className={`${styles.animate_fade_in} h-full`}>
          <Card className={`${styles.controlsCard} h-full`}>
            <CardContent className={styles.p_6}>
              <VideoCrop
                ref={videoRef}
                videoUrl={videoUrl}
                onCropChange={setCropData}
                trimSettings={trimSettings}
                rotation={rotation}
                handleVideoLoad={handleVideoLoad}
                onVideoDataLoaded={onVideoDataLoaded}
                hasVideoPoster={hasVideoPoster}
              />

              {/* Rotation Controls */}
              <div className={`${styles.rotateControls}`}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRotateCounterClockwise}
                >
                  <Rotate90DegreesCcw fontSize="small" sx={{ mr: 1 }} />
                  Rotate Left
                </Button>

                <span className={`${styles.rotation} `}>{rotation}°</span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRotateClockwise}
                >
                  <Rotate90DegreesCw fontSize="small" sx={{ mr: 1 }} />
                  Rotate Right
                </Button>
              </div>

              {videoDuration > 0 && (
                <div>
                  <VideoTimeline
                    duration={videoDuration}
                    trimSettings={trimSettings}
                    onTrimChange={handleTrimChange}
                    videoRef={videoRef}
                  />

                  <div className={`${styles.saveEditsContainer} `}>
                    <Button
                      onClick={handleExport}
                      disabled={isProcessing || trimSettings.duration <= 0}
                      size="sm"
                      variant="contained"
                      sx={{ color: "white" }}
                      className={isProcessing ? styles.processingButton : ""}
                    >
                      {isProcessing ? null : (
                        <Save fontSize="small" sx={{ mr: 1 }} />
                      )}

                      {isProcessing
                        ? `Processing... ${progress}%`
                        : "Save Edits"}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default VideoTrimmer;
